/**
 * Palette de busca global estilo spotlight (Cmd+K) — story E3.2.1.
 *
 * Montado uma vez, global, nas rotas autenticadas (App.tsx). Abre por ⌘K/Ctrl+K
 * ou pelo botão do header (via ``globalSearchStore``). Input com debounce 300ms
 * alimenta ``useGlobalSearch``; resultados agrupados por tipo, com highlight, e
 * o clique navega para a coleção do módulo da entidade.
 *
 * cmdk roda com ``shouldFilter={false}`` — a ordenação/relevância é do servidor
 * (ts_rank); deixar o filtro client-side ligado esconderia resultados válidos.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, Search } from "lucide-react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useDebounce } from "@/hooks/useDebounce";
import {
  ENTITY_TYPE_META,
  SEARCHABLE_ENTITY_TYPES,
  entityRoute,
  isSearchableEntityType,
  type SearchableEntityType,
} from "@/lib/globalSearch";
import {
  setGlobalSearchOpen,
  toggleGlobalSearch,
  useGlobalSearchOpen,
} from "@/stores/globalSearchStore";

import SearchResultItem from "./SearchResultItem";
import { MIN_QUERY_LENGTH, useGlobalSearch, type GlobalSearchResult } from "./useGlobalSearch";

const GlobalSearchBar: React.FC = () => {
  const open = useGlobalSearchOpen();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const debounced = useDebounce(value, 300);
  const { data, isFetching, isError } = useGlobalSearch(debounced);

  // Atalho global ⌘K / Ctrl+K (toggle). toggleGlobalSearch lê o estado atual
  // da store, então o listener pode ser registrado uma única vez.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggleGlobalSearch();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Limpa o termo ao fechar — reabrir começa do zero.
  useEffect(() => {
    if (!open) setValue("");
  }, [open]);

  const grouped = useMemo(() => {
    const map = new Map<SearchableEntityType, GlobalSearchResult[]>();
    for (const r of data?.results ?? []) {
      // Defensivo: o endpoint já restringe via entity_types, mas só agrupamos
      // tipos com rota navegável (nunca renderiza item sem destino).
      if (!isSearchableEntityType(r.entity_type)) continue;
      const arr = map.get(r.entity_type) ?? [];
      arr.push(r);
      map.set(r.entity_type, arr);
    }
    return map;
  }, [data]);

  const handleSelect = (result: GlobalSearchResult) => {
    const route = entityRoute(result.entity_type);
    if (!route) return;
    setGlobalSearchOpen(false);
    navigate(route);
  };

  const trimmed = debounced.trim();
  const canSearch = trimmed.length >= MIN_QUERY_LENGTH;
  const hasResults = (data?.results.length ?? 0) > 0;
  const showInitial = trimmed.length === 0;
  const showTooShort = trimmed.length > 0 && !canSearch;
  // "Vazio" só quando a resposta carregada é DESTA query (data.query é ecoado
  // pelo backend) — evita flash de "nenhum resultado" no 1º fetch (data ainda
  // undefined) e evita confundir com dados stale do keepPreviousData no refetch.
  const showEmpty = canSearch && !isFetching && !isError && data?.query === trimmed && !hasResults;
  // Fallback de typo (E3.2.2): o FTS exato não achou nada e os resultados vieram do
  // pg_trgm. Sinaliza ao usuário por que aparecem termos diferentes do digitado.
  // Atrelado a data.query === trimmed (não mostrar com dados stale do keepPreviousData).
  const showFuzzy = canSearch && !isError && !!data?.fuzzy && hasResults && data?.query === trimmed;

  return (
    <Dialog open={open} onOpenChange={setGlobalSearchOpen}>
      <DialogContent className="overflow-hidden p-0 shadow-lg sm:max-w-2xl">
        <DialogTitle className="sr-only">Busca global</DialogTitle>
        <DialogDescription className="sr-only">
          Busque documentos, mensagens, tarefas e publicações em todo o sistema.
        </DialogDescription>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12"
        >
          <CommandInput
            value={value}
            onValueChange={setValue}
            placeholder="Buscar em documentos, mensagens, tarefas e feed…"
          />
          <CommandList>
            {isFetching && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Buscando…
              </div>
            )}

            {isError && !isFetching && (
              <div className="py-6 text-center text-sm text-destructive">
                Não foi possível buscar agora. Tente novamente.
              </div>
            )}

            {showInitial && !isFetching && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Comece a digitar para buscar em todo o sistema.
              </div>
            )}

            {showTooShort && !isFetching && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Digite ao menos {MIN_QUERY_LENGTH} caracteres.
              </div>
            )}

            {showEmpty && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nenhum resultado encontrado.
              </div>
            )}

            {showFuzzy && (
              <div className="border-b px-3 py-2 text-xs text-muted-foreground">
                Nenhum resultado exato. Mostrando resultados aproximados para{" "}
                <span className="font-medium text-foreground">“{trimmed}”</span>.
              </div>
            )}

            {!isError &&
              canSearch &&
              SEARCHABLE_ENTITY_TYPES.map((type) => {
                const items = grouped.get(type);
                if (!items || items.length === 0) return null;
                return (
                  <CommandGroup key={type} heading={ENTITY_TYPE_META[type].label}>
                    {items.map((r) => (
                      <SearchResultItem
                        key={`${r.entity_type}:${r.entity_id}`}
                        entityType={r.entity_type}
                        entityId={r.entity_id}
                        title={r.title}
                        snippet={r.snippet}
                        onSelect={() => handleSelect(r)}
                      />
                    ))}
                  </CommandGroup>
                );
              })}

            {data?.has_more && hasResults && canSearch && (
              <div className="border-t px-3 py-2 text-center text-xs text-muted-foreground">
                Mostrando os primeiros resultados — refine a busca para ver mais.
              </div>
            )}

            {/* Escape hatch: a palette cobre documentos/mensagens/tarefas/feed.
                Colaboradores, lojas e equipamentos vivem na busca avançada
                (/busca/resultados) — preserva a cobertura da antiga
                GlobalSearchBox sem um 2º campo de busca no header. O termo
                digitado é repassado via ?q=. */}
            {!isFetching && !isError && (
              <CommandGroup heading="Outros módulos">
                <CommandItem
                  value="__busca_avancada__"
                  onSelect={() => {
                    setGlobalSearchOpen(false);
                    navigate(
                      trimmed
                        ? `/busca/resultados?q=${encodeURIComponent(trimmed)}`
                        : "/busca/resultados"
                    );
                  }}
                  className="cursor-pointer"
                >
                  <Search className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
                  <span>Colaboradores, lojas e equipamentos</span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                    Busca avançada
                    <ArrowUpRight className="h-3 w-3" aria-hidden />
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearchBar;
